/**
 * Approximate seasonal windows + "where are they now" along each route (0 = route start, 1 = end).
 * Calibrated loosely to Journey North–style timing; illustrative not survey-grade.
 */

export type MigrationActivity =
  | 'peak'
  | 'active'
  | 'approaching'
  | 'complete'
  | 'off-season';

export interface SeasonalState {
  /** 0–1 along waypoints start→end; null if not on main migration leg */
  progress: number | null;
  activity: MigrationActivity;
  /** Lower opacity / desaturate when false */
  visuallyActive: boolean;
  /** Human-readable "where now" blurb */
  nowLabel: string;
  /** Status line for panel */
  statusLine: string;
  /** Animated dash flows toward route end, or reversed (southbound leg) */
  dashReversed: boolean;
}

function inRange(
  d: Date,
  m0: number,
  d0: number,
  m1: number,
  d1: number
): boolean {
  const y = d.getFullYear();
  const a = new Date(y, m0 - 1, d0).getTime();
  const b = new Date(y, m1 - 1, d1).getTime();
  const t = d.getTime();
  return t >= a && t <= b;
}

function lerpProgress(
  d: Date,
  m0: number,
  d0: number,
  m1: number,
  d1: number
): number {
  const y = d.getFullYear();
  const a = new Date(y, m0 - 1, d0).getTime();
  const b = new Date(y, m1 - 1, d1).getTime();
  return Math.max(0, Math.min(1, (d.getTime() - a) / (b - a)));
}

/** March 18 examples: gray whale ~0.45 CA coast; monarch early north ~0.12 */
const SEASON: Record<
  string,
  {
    windows: Array<{
      start: [number, number];
      end: [number, number];
      peak?: [number, number];
      nowTemplate: string;
      /** true = progress runs end→start of waypoint list (southbound) */
      invertProgress?: boolean;
    }>;
    offSeasonLabel: string;
    dormantProgress?: number | null;
  }
> = {
  'gray-whale': {
    windows: [
      {
        start: [2, 1],
        end: [5, 15],
        peak: [3, 1],
        nowTemplate: 'Northbound — many off the Pacific coast here',
      },
    ],
    offSeasonLabel: 'Mostly in Arctic feeding grounds or southbound (Oct–Jan)',
    dormantProgress: 0.92,
  },
  monarch: {
    windows: [
      {
        start: [3, 1],
        end: [5, 20],
        peak: [4, 10],
        nowTemplate: 'Waves moving up the central US flyway',
      },
      {
        start: [9, 1],
        end: [11, 15],
        peak: [10, 15],
        nowTemplate: 'Southbound toward Michoacán',
        invertProgress: true,
      },
    ],
    offSeasonLabel: 'Winter roosts (Mexico) or summer breeding — not mass migration',
    dormantProgress: 0.05,
  },
  hummingbird: {
    windows: [
      {
        start: [3, 1],
        end: [5, 25],
        peak: [4, 1],
        nowTemplate: 'Crossing the Gulf; leading edge in the Southeast',
      },
    ],
    offSeasonLabel: 'Tropical wintering or northern breeding',
    dormantProgress: 0.08,
  },
  swallows: {
    windows: [
      {
        start: [2, 15],
        end: [4, 30],
        peak: [3, 19],
        nowTemplate: 'Arriving California coast; peak around Capistrano',
      },
    ],
    offSeasonLabel: 'Nesting season or southbound to South America',
    dormantProgress: 0.85,
  },
  'sandhill-crane': {
    windows: [
      {
        start: [3, 1],
        end: [4, 25],
        peak: [3, 20],
        nowTemplate: 'Staging on the Platte; some already pushing north',
      },
    ],
    offSeasonLabel: 'Arctic summer or southern wintering',
    dormantProgress: 0.35,
  },
  'arctic-tern': {
    windows: [
      {
        start: [5, 1],
        end: [7, 31],
        peak: [6, 15],
        nowTemplate: 'Arctic breeding season — adults on nesting grounds',
      },
    ],
    offSeasonLabel: 'Southern Ocean feeding or long ocean commutes',
    dormantProgress: 0.55,
  },
  wildebeest: {
    windows: [
      {
        start: [1, 1],
        end: [12, 31],
        peak: [6, 1],
        nowTemplate: 'Serengeti–Mara circuit — position shifts with rains',
      },
    ],
    offSeasonLabel: '',
    dormantProgress: null,
  },
  caribou: {
    windows: [
      {
        start: [4, 1],
        end: [5, 31],
        peak: [4, 20],
        nowTemplate: 'Spring movement toward calving on the coastal plain',
      },
      {
        start: [9, 1],
        end: [10, 15],
        peak: [9, 20],
        nowTemplate: 'Fall migration south to winter range',
        invertProgress: true,
      },
    ],
    offSeasonLabel: 'On summer or winter range',
    dormantProgress: 0.5,
  },
  'humpback-pac': {
    windows: [
      {
        start: [3, 1],
        end: [5, 31],
        peak: [4, 15],
        nowTemplate: 'Hawaii whales moving up — many mid-Pacific / approaching Alaska',
      },
    ],
    offSeasonLabel: 'Alaska feeding or Hawaii breeding grounds',
    dormantProgress: 0.15,
  },
  'humpback-atl': {
    windows: [
      {
        start: [2, 15],
        end: [5, 15],
        peak: [3, 20],
        nowTemplate: 'Caribbean population heading to North Atlantic feeding',
      },
    ],
    offSeasonLabel: 'High-latitude feeding or tropical calving',
    dormantProgress: 0.2,
  },
  'leatherback-pac': {
    windows: [
      {
        start: [4, 1],
        end: [8, 31],
        peak: [6, 1],
        nowTemplate: 'Pacific leatherbacks spread across basin — foraging corridors',
      },
    ],
    offSeasonLabel: 'Nesting beaches or open-ocean foraging',
    dormantProgress: 0.45,
  },
  'salmon-pac': {
    windows: [
      {
        start: [7, 1],
        end: [11, 15],
        peak: [9, 15],
        nowTemplate: 'River returns — timing varies by run and river',
      },
    ],
    offSeasonLabel: 'Ocean phase or spawning complete',
    dormantProgress: 0.1,
  },
  godwit: {
    windows: [
      {
        start: [9, 1],
        end: [11, 15],
        peak: [10, 1],
        nowTemplate: 'Alaska bar-tailed godwits staging for nonstop flight to NZ',
      },
    ],
    offSeasonLabel: 'New Zealand summer or Alaska tundra breeding prep',
    dormantProgress: 0.08,
  },
  'eel-european': {
    windows: [
      {
        start: [2, 1],
        end: [6, 30],
        peak: [4, 1],
        nowTemplate: 'Glass eels moving into European rivers',
      },
    ],
    offSeasonLabel: 'Sargasso spawning or yellow eels in freshwater',
    dormantProgress: 0.75,
  },
  'red-crab': {
    windows: [
      {
        start: [10, 1],
        end: [12, 15],
        peak: [11, 1],
        nowTemplate: 'Red crab spawning migration on Christmas Island',
      },
    ],
    offSeasonLabel: 'Forest phase on the island',
    dormantProgress: 0.5,
  },
  'darner-dragonfly': {
    windows: [
      {
        start: [8, 15],
        end: [10, 15],
        peak: [9, 15],
        nowTemplate: 'Green darners moving south in multigenerational relay',
      },
    ],
    offSeasonLabel: 'Aquatic nymphs or early-season northbound',
    dormantProgress: 0.4,
  },
};

function inPeak(
  d: Date,
  peak: [number, number] | undefined,
  win: { start: [number, number]; end: [number, number] }
): boolean {
  if (!peak) return false;
  const y = d.getFullYear();
  const ps = new Date(y, peak[0] - 1, peak[1]).getTime();
  const pe = new Date(y, win.end[0] - 1, win.end[1]).getTime();
  const mid = (ps + Math.min(pe, ps + 20 * 86400000)) / 2;
  return Math.abs(d.getTime() - mid) < 18 * 86400000;
}

export function getSeasonalState(
  routeId: string,
  date = new Date()
): SeasonalState {
  const cfg = SEASON[routeId];
  if (!cfg) {
    return {
      progress: 0.5,
      activity: 'active',
      visuallyActive: true,
      nowLabel: 'Along migration corridor',
      statusLine: 'Migration timing varies.',
      dashReversed: false,
    };
  }

  for (const w of cfg.windows) {
    if (inRange(date, w.start[0], w.start[1], w.end[0], w.end[1])) {
      let progress = lerpProgress(
        date,
        w.start[0],
        w.start[1],
        w.end[0],
        w.end[1]
      );
      if (w.invertProgress) progress = 1 - progress;
      let activity: MigrationActivity = 'active';
      if (inPeak(date, w.peak, w)) activity = 'peak';
      else if (progress < 0.15) activity = 'approaching';
      else if (progress > 0.85) activity = 'complete';

      return {
        progress,
        activity,
        visuallyActive: true,
        nowLabel: w.nowTemplate,
        statusLine:
          activity === 'peak'
            ? 'Peak movement right now'
            : activity === 'approaching'
              ? 'Migration ramping up'
              : activity === 'complete'
                ? 'Tail end of this movement window'
                : 'Active migration season',
        dashReversed: !!w.invertProgress,
      };
    }
  }

  const p =
    cfg.dormantProgress !== undefined && cfg.dormantProgress !== null
      ? cfg.dormantProgress
      : null;
  return {
    progress: p,
    activity: 'off-season',
    visuallyActive: false,
    nowLabel: cfg.offSeasonLabel || 'Outside main migration window',
    statusLine: 'Not in primary migration season',
    dashReversed: false,
  };
}

export function getSeasonBadge(routeId: string, date = new Date()): string {
  const s = getSeasonalState(routeId, date);
  if (!s.visuallyActive) return '';
  if (s.activity === 'peak') return 'Peak';
  if (s.activity === 'active' || s.activity === 'approaching')
    return 'In season';
  if (s.activity === 'complete') return 'Winding down';
  return '';
}

/** Interpolate along the polyline: t in [0,1] maps linearly from first to last waypoint (equal weight per segment). */
export function interpolateAlongRoute(
  waypoints: [number, number][],
  t: number
): [number, number] {
  if (waypoints.length === 0) return [0, 0];
  if (waypoints.length === 1) return waypoints[0];
  const tt = Math.max(0, Math.min(1, t));
  const nSeg = waypoints.length - 1;
  const segT = tt * nSeg;
  const i = Math.min(Math.floor(segT), nSeg - 1);
  const u = i === nSeg - 1 && tt >= 1 ? 1 : segT - i;
  const [lat0, lng0] = waypoints[i];
  const [lat1, lng1] = waypoints[i + 1];
  return [
    lat0 + (lat1 - lat0) * u,
    lng0 + (lng1 - lng0) * u,
  ];
}
